import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import httpService from "../../../../services/httpServices";
import useAffiliateDashboard from "./hooks/useAffiliateDashboard";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  MousePointerClick,
  Users,
  TrendingUp,
  DollarSign,
  RefreshCw,
  Settings,
  CreditCard,
  Link2,
  Copy,
  Check,
  QrCode,
  Award,
  AlertTriangle,
  Crown,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import toast from "react-hot-toast";
import SocialShareButtons from "./SocialShareButtons";
import { resolveRankFromConnect } from "./connectRankUtil";

/**
 * Rank-based affiliate dashboard (P5-H).
 *
 *   Prominent rank card at the top (name + commission %)
 *   4 stat cards (clicks, conversions, rate, pending balance with $100 progress)
 *   Lifetime earnings strip
 *   Activity chart + tabbed feed (All / Clicks / Conversions / Payments)
 *   "Coming Soon" note for the automated BlockBot Pay rail
 */
const AffiliateDashboard = ({ affiliate }) => {
  const [dashData, setDashData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartRange, setChartRange] = useState("30");
  const [conversions, setConversions] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [activityTab, setActivityTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [connect, setConnect] = useState(null);
  const qrRef = useRef(null);

  useAffiliateDashboard(affiliate?._id, () => {
    fetchDashboard();
    fetchChart();
  });

  const fetchDashboard = async () => {
    try {
      const [dashRes, convRes, clickRes, payoutRes] = await Promise.all([
        httpService.get("/affiliate/dashboard"),
        httpService.get("/affiliate/conversions?limit=10"),
        httpService.get("/affiliate/clicks?limit=10"),
        httpService.get("/affiliate/payouts?limit=10"),
      ]);
      setDashData(dashRes.data);
      setConversions(convRes.data || []);
      setClicks(clickRes.data || []);
      setPayouts(payoutRes.data || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  const fetchChart = async () => {
    try {
      const res = await httpService.get(`/affiliate/chart?range=${chartRange}`);
      setChartData(res.data);
    } catch {
      /* silent */
    }
  };

  const fetchConnectLookup = async () => {
    try {
      const res = await httpService.get("/affiliate/connect-lookup");
      setConnect(res.data);
    } catch {
      /* silent — header falls back to stored rank */
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchConnectLookup();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    fetchChart();
    // eslint-disable-next-line
  }, [chartRange]);

  const shareLink = dashData?.shareLink || "";
  // Prefer the live Connect lookup rank — the stored affiliate.rank only
  // refreshes on the daily sync job, so it can lag behind Connect by a day.
  const connectRank = resolveRankFromConnect(connect);
  const rank = connectRank?.rank || dashData?.rank || "none";
  const rankDisplay = connectRank?.rankDisplay || dashData?.rankDisplay || "Unranked";
  const commissionRate = connectRank?.commissionRate ?? dashData?.commissionRate ?? 0.2;
  const eligibleRewards = dashData?.eligibleRewards || [];
  const payoutRail =
    connectRank?.rank && connectRank.rank !== "none"
      ? "rally"
      : dashData?.payoutRail || (rank === "none" ? "stripe" : "rally");
  const canReceive = !!dashData?.canReceivePayout;
  const missingRallyWallet = rank !== "none" && !dashData?.rallyWalletAddress;
  const missingStripeAccount = rank === "none" && !dashData?.stripeConnectedAccountId;
  const missingDestination = missingRallyWallet || missingStripeAccount;

  const copyLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `rlink-referral-qr-${affiliate.shareSlug}.png`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 text-[#7700FF] animate-spin" />
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-3 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">Affiliate Dashboard</h2>
          <p className="text-gray-400 text-sm">
            Track your referrals, commissions, and payments.
          </p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/dashboard/affiliate/links"
            className="flex items-center gap-2 bg-gradient-to-r from-[#7700FF] to-[#00c853] hover:opacity-90 text-white px-4 py-2.5 rounded-lg font-bold text-sm transition-opacity"
          >
            <Link2 className="w-4 h-4" /> Share Link
          </Link>
          <Link
            to="/dashboard/affiliate/payouts"
            className="flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors"
          >
            <CreditCard className="w-4 h-4" /> Payments
          </Link>
          <Link
            to="/dashboard/affiliate/settings"
            className="flex items-center justify-center w-10 h-10 bg-white/5 border border-white/10 hover:bg-white/10 text-gray-400 rounded-lg transition-colors"
          >
            <Settings className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* RANK CARD — top of dashboard, the prominent commission surface */}
      <RankCard
        rank={rank}
        rankDisplay={rankDisplay}
        commissionRate={commissionRate}
        eligibleRewards={eligibleRewards}
        payoutRail={payoutRail}
      />

      {/* Missing destination warning */}
      {missingDestination && (dashData?.pendingBalance || 0) > 0 && (
        <div className="bg-[#ffa500]/10 border border-[#ffa500]/30 rounded-2xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#ffa500] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-[#ffa500] font-semibold">
              {payoutRail === "rally"
                ? "Add your Rally wallet to receive Rally token payments"
                : "Connect Stripe to receive USD payments"}
            </p>
            <p className="text-gray-400 text-sm">
              Your pending balance will be queued for payment once your {payoutRail === "rally" ? "Rally wallet" : "Stripe account"} is on file.
            </p>
          </div>
          <Link
            to="/dashboard/affiliate/payouts"
            className="bg-[#ffa500]/20 border border-[#ffa500]/40 text-[#ffa500] px-4 py-2 rounded-lg font-bold text-sm hover:bg-[#ffa500]/30 transition-colors"
          >
            Set up
          </Link>
        </div>
      )}

      {/* Hero: Share link + QR + social */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
          <div className="space-y-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">
                Your Share Link
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 bg-white/5 rounded-lg px-4 py-3 border border-white/10 min-w-0">
                  <p className="text-[#00c853] font-medium text-sm break-all">{shareLink}</p>
                </div>
                <button
                  onClick={copyLink}
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#7700FF] to-[#00c853] hover:opacity-90 text-white px-5 py-3 rounded-lg font-bold transition-opacity whitespace-nowrap"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
                Share to
              </span>
              <SocialShareButtons url={shareLink} />
            </div>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div ref={qrRef} className="bg-white p-3 rounded-xl">
              <QRCodeCanvas value={shareLink || "https://r-link.com"} size={120} level="M" />
            </div>
            <button
              onClick={downloadQR}
              className="text-xs text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
            >
              <QrCode className="w-3 h-3" /> Download
            </button>
          </div>
        </div>
      </div>

      {/* Stat cards — 4 cards for everyone */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={MousePointerClick}
          label="Total Clicks"
          value={dashData?.clicks ?? 0}
          color="#7700FF"
        />
        <StatCard
          icon={Users}
          label="Conversions"
          value={dashData?.conversions ?? 0}
          color="#00c853"
        />
        <StatCard
          icon={TrendingUp}
          label="Conversion Rate"
          value={`${dashData?.conversionRate ?? 0}%`}
          color="#3b82f6"
        />
        <PendingBalanceCard
          balance={dashData?.pendingBalance || 0}
          threshold={dashData?.payoutThreshold || 100}
          progress={dashData?.payoutProgress || 0}
          payoutRail={payoutRail}
          canReceive={canReceive}
        />
      </div>

      {/* Lifetime earnings strip */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#00c853]/20 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-[#00c853]" />
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
              Lifetime Earnings
            </p>
            <p className="text-2xl font-bold text-white">
              ${(dashData?.lifetimeEarnings || 0).toFixed(2)}
            </p>
          </div>
        </div>
        {dashData?.lastPayout && (
          <div className="text-right">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
              Last Payment
            </p>
            <p className="text-sm font-semibold text-white">
              ${(dashData.lastPayout.amount || 0).toFixed(2)}
            </p>
            <p className="text-xs text-gray-500">
              {new Date(dashData.lastPayout.date).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-white">Activity</h3>
          <div className="flex gap-1 bg-white/5 rounded-lg p-1">
            {["7", "30", "90"].map((r) => (
              <button
                key={r}
                onClick={() => setChartRange(r)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  chartRange === r
                    ? "bg-[#7700FF] text-white"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                {r}d
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#556177" }} />
            <YAxis tick={{ fontSize: 11, fill: "#556177" }} />
            <Tooltip
              contentStyle={{
                backgroundColor: "#060621",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "8px",
                color: "#fff",
              }}
            />
            <Line type="monotone" dataKey="clicks" stroke="#7700FF" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="conversions" stroke="#00c853" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
        <div className="flex items-center gap-6 mt-4">
          <Legend color="#7700FF" label="Clicks" />
          <Legend color="#00c853" label="Conversions" />
        </div>
      </div>

      {/* Activity feed (tabbed) */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1 w-fit mb-6 flex-wrap">
          <TabButton active={activityTab === "all"} onClick={() => setActivityTab("all")}>All</TabButton>
          <TabButton active={activityTab === "clicks"} onClick={() => setActivityTab("clicks")}>Clicks</TabButton>
          <TabButton active={activityTab === "conversions"} onClick={() => setActivityTab("conversions")}>Conversions</TabButton>
          <TabButton active={activityTab === "payouts"} onClick={() => setActivityTab("payouts")}>Payments</TabButton>
        </div>
        {(activityTab === "all" || activityTab === "conversions") && (
          <ConversionsTable conversions={conversions} />
        )}
        {(activityTab === "all" || activityTab === "clicks") && (
          <ClicksTable clicks={clicks} className={activityTab === "all" ? "mt-8" : ""} />
        )}
        {activityTab === "payouts" && <PaymentsTable payouts={payouts} />}
      </div>
    </div>
  );
};

// ── RankCard — the headline UI for the rank-based model ───────────────
const RankCard = ({ rank, rankDisplay, commissionRate, eligibleRewards, payoutRail }) => {
  const percent = Math.round(commissionRate * 100);
  const isRanked = rank !== "none";
  return (
    <div className="bg-gradient-to-r from-[#7700FF]/15 to-[#00c853]/10 backdrop-blur-xl border border-[#7700FF]/30 rounded-2xl p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#7700FF]/25 flex items-center justify-center flex-shrink-0">
            <Crown className="w-7 h-7 text-[#7700FF]" />
          </div>
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">
              Your Rank {isRanked ? "(via Connect)" : ""}
            </p>
            <p className="text-3xl font-bold text-white">{rankDisplay}</p>
          </div>
        </div>
        <div className="flex items-center gap-4 md:justify-end">
          <div className="w-14 h-14 rounded-2xl bg-[#00c853]/25 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-7 h-7 text-[#00c853]" />
          </div>
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">
              Commission on every referral
            </p>
            <p className="text-4xl font-bold text-white">{percent}%</p>
          </div>
        </div>
      </div>
      {/* Rewards + rail info */}
      <div className="mt-5 pt-5 border-t border-white/10 flex flex-wrap items-center gap-4">
        {isRanked && eligibleRewards.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
              Eligible Rewards
            </span>
            {eligibleRewards.map((r) => (
              <span
                key={r}
                className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-gray-200 font-mono font-bold flex items-center gap-1"
              >
                <Award className="w-3 h-3 text-[#00c853]" /> {r}
              </span>
            ))}
          </div>
        )}
        <div className="ml-auto flex items-center gap-2 text-xs">
          {payoutRail === "rally" ? (
            <>
              <span className="px-2.5 py-1 rounded-full bg-[#00c853]/20 text-[#00c853] font-semibold">
                Paid in Rally Tokens
              </span>
              <span className="text-gray-400">
                Automated via BlockBot Pay — <span className="italic">Coming Soon</span>
              </span>
            </>
          ) : (
            <>
              <span className="px-2.5 py-1 rounded-full bg-[#3b82f6]/20 text-[#3b82f6] font-semibold">
                Paid via Stripe (USD)
              </span>
              <span className="text-gray-400">
                Released once your balance reaches $100
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
    <div className="flex items-center gap-3 mb-3">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${color}20` }}
      >
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <span className="text-sm text-gray-400">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

const PendingBalanceCard = ({ balance, threshold, progress, payoutRail, canReceive }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-9 h-9 rounded-lg bg-[#ffa500]/20 flex items-center justify-center">
        <DollarSign className="w-5 h-5 text-[#ffa500]" />
      </div>
      <span className="text-sm text-gray-400">Pending Balance</span>
    </div>
    <p className="text-2xl font-bold text-white mb-2">${balance.toFixed(2)}</p>
    <div className="w-full bg-white/10 rounded-full h-2 mb-1">
      <div
        className="bg-gradient-to-r from-[#7700FF] to-[#00c853] h-2 rounded-full transition-all duration-500"
        style={{ width: `${Math.min(100, progress)}%` }}
      />
    </div>
    <p className="text-xs text-gray-500">
      {balance >= threshold
        ? canReceive
          ? `Over $${threshold} — ready to pay via ${payoutRail === "rally" ? "Rally" : "Stripe"}`
          : `Over $${threshold} — add your ${payoutRail === "rally" ? "wallet" : "Stripe account"}`
        : `$${(threshold - balance).toFixed(2)} to reach $${threshold} minimum`}
    </p>
  </div>
);

const Legend = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
    <span className="text-xs text-gray-400">{label}</span>
  </div>
);

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-1.5 rounded-md text-sm transition-colors ${
      active ? "bg-[#7700FF] text-white" : "text-gray-400 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const ConversionsTable = ({ conversions }) => {
  if (conversions.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400 mb-1">No conversions yet</p>
        <p className="text-gray-500 text-sm">Share your link to get started!</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left py-3 text-gray-500 font-medium">Date</th>
            <th className="text-left py-3 text-gray-500 font-medium">Plan</th>
            <th className="text-right py-3 text-gray-500 font-medium">Sale</th>
            <th className="text-right py-3 text-gray-500 font-medium">Months Paid</th>
            <th className="text-right py-3 text-gray-500 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {conversions.map((c) => (
            <tr key={c._id} className="border-b border-white/5">
              <td className="py-3 text-gray-300">
                {new Date(c.convertedAt).toLocaleDateString()}
              </td>
              <td className="py-3 text-gray-300">{c.subscriptionTier || "—"}</td>
              <td className="py-3 text-right text-white">${(c.saleAmount || 0).toFixed(2)}</td>
              <td className="py-3 text-right text-gray-300">{c.monthsPaid || 0}/12</td>
              <td className="py-3 text-right">
                <span
                  className={`rounded-full text-xs font-bold px-3 py-1 ${
                    c.status === "active"
                      ? "bg-[#00c853]/20 text-[#00c853]"
                      : c.status === "completed"
                      ? "bg-[#7700FF]/20 text-[#7700FF]"
                      : "bg-red-500/20 text-red-400"
                  }`}
                >
                  {c.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ClicksTable = ({ clicks, className = "" }) => {
  if (clicks.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <p className="text-gray-400">No clicks recorded yet.</p>
      </div>
    );
  }
  return (
    <div className={`overflow-x-auto ${className}`}>
      <h4 className="text-sm font-semibold text-gray-300 mb-3">Recent Clicks</h4>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left py-3 text-gray-500 font-medium">Time</th>
            <th className="text-left py-3 text-gray-500 font-medium">Source</th>
            <th className="text-left py-3 text-gray-500 font-medium">Campaign</th>
          </tr>
        </thead>
        <tbody>
          {clicks.map((c) => (
            <tr key={c._id} className="border-b border-white/5">
              <td className="py-3 text-gray-300">{new Date(c.clickedAt).toLocaleString()}</td>
              <td className="py-3 text-gray-300 truncate max-w-[280px]">
                {c.sourceUrl || "Direct"}
              </td>
              <td className="py-3 text-gray-300">{c.utmCampaign || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Payments table (unified Rally + Stripe from AffiliatePayment) ──
const PaymentsTable = ({ payouts }) => {
  if (payouts.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400 mb-1">No payments yet</p>
        <p className="text-gray-500 text-sm">
          Payments are released once your pending balance reaches $100.
        </p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left py-3 text-gray-500 font-medium">Date</th>
            <th className="text-left py-3 text-gray-500 font-medium">Rail</th>
            <th className="text-left py-3 text-gray-500 font-medium">Destination / Tx</th>
            <th className="text-right py-3 text-gray-500 font-medium">Amount (USD)</th>
            <th className="text-right py-3 text-gray-500 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {payouts.map((p) => {
            const rail = p.rail || "rally";
            const destOrTx =
              rail === "rally"
                ? p.blockchainTxId || p.walletAddress || "—"
                : p.stripePayoutId || p.stripeConnectedAccountId || "—";
            return (
              <tr key={p._id} className="border-b border-white/5">
                <td className="py-3 text-gray-300">
                  {new Date(p.paidAt || p.createdAt).toLocaleDateString()}
                </td>
                <td className="py-3 text-gray-300 uppercase text-xs font-bold">
                  <span
                    className={`px-2 py-0.5 rounded-full ${
                      rail === "rally"
                        ? "bg-[#00c853]/20 text-[#00c853]"
                        : "bg-[#3b82f6]/20 text-[#3b82f6]"
                    }`}
                  >
                    {rail}
                  </span>
                </td>
                <td className="py-3 text-gray-300 font-mono text-xs truncate max-w-[220px]">
                  {destOrTx}
                </td>
                <td className="py-3 text-right font-bold text-white">
                  ${(p.usdAmount || 0).toFixed(2)}
                </td>
                <td className="py-3 text-right">
                  <span
                    className={`rounded-full text-xs font-bold px-3 py-1 ${
                      p.status === "paid"
                        ? "bg-[#00c853]/20 text-[#00c853]"
                        : p.status === "failed" || p.status === "cancelled"
                        ? "bg-red-500/20 text-red-400"
                        : "bg-[#ffa500]/20 text-[#ffa500]"
                    }`}
                  >
                    {p.status.replace(/_/g, " ")}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AffiliateDashboard;
